import React, { useState, useEffect } from 'react';
import { nanoid } from 'nanoid';
import { Link, useHistory } from 'react-router-dom';
import {
  Input,
  Button,
  Details,
  Table,
  Hint,
  ErrorSummary,
} from 'govuk-react-jsx';
import { useSelector, useDispatch } from 'react-redux';
import evcgClose from '../../images/evcgClose.jpg';
import evcgLong from '../../images/evcgLong.jpg';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../../features/app/appSlice';
import {
  getGrantTypeSelected,
  getChosenChargePoint,
  setApplicationClaimId,
  updateChargepointDetails,
} from '../../features/installer/installerJourneySlice';
import { checkValidSerialNumber } from '../../regex/regex';
import {
  updateCpDetails,
  deletePhoto,
  validateCpSerialNum,
  getConfigurableDeadlines,
} from '../../firebase';
import { getApplicationDocumentId } from '../../features/application/applicationJourneySlice';
import readFileAsDataURL from '../../utils/readFileAsDataUrl';
import ChargepointSelect from './ChargepointSelect';
import useTitle from '../../hooks/useTitle';

const AddAdditionalDetails = () => {
  const chargePointRedux = useSelector(getChosenChargePoint);
  const grantTypeSelected = useSelector(getGrantTypeSelected);
  const [errorUploadedFile, setErrorUploadedFile] = useState('');
  const [indexId, setIndexId] = useState(0);
  const [errorSerialNumber, setErrorSerialNumber] = useState();
  const [manufacturer, setManufacturer] = useState(
    chargePointRedux.manufacturer
  );
  const [grantType] = useState(grantTypeSelected);
  const [chargepointWordingChangeDate, setChargepointWordingChangeDate] =
    useState('');
  const [model, setModel] = useState(chargePointRedux.model);
  const [socket, setSocket] = useState(chargePointRedux.socket);
  const [hasChargepointSelectError, setHasChargepointSelectError] =
    useState(false);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [serialNumber, setSerialNumber] = useState(
    chargePointRedux.serialNumber || ''
  );
  const [photos, setPhotos] = useState(chargePointRedux.photos || []);
  const [errorList, setErrorList] = useState([]);
  const [selectChargepointError, setSelectChargepointError] = useState([]);
  const errorArray = [];

  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;

  useTitle(
    'installer.AddAdditionalDetails.s29',
    'Add serial number and photographs - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  const PHOTO_FILE_SIZE = 10 * 1000 * 1000;
  const MIN_UPLOAD_FILES =
    chargepointWordingChangeDate && grantType === 'LANDLORD_RESIDENTIAL_GRANT'
      ? 4
      : 2;
  const MAX_UPLOAD_FILES = 10;

  const dispatch = useDispatch();
  const history = useHistory();
  const documentId = useSelector(getApplicationDocumentId);

  useEffect(async () => {
    const configurableDeadlines = await getConfigurableDeadlines();
    if (configurableDeadlines?.data) {
      setChargepointWordingChangeDate(
        configurableDeadlines.data?.chargepointWordingChangeDate
      );
    }
    if (selectChargepointError.length !== 0) {
      selectChargepointError.map((error) => {
        if (
          errorList.filter((err) => err.children === error.text.children)
            .length === 0
        ) {
          setErrorList([...errorList, { ...error.text, href: error.href }]);
        }
        return errorList;
      });
    }
  }, [selectChargepointError, errorList]);

  const validFileTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/bmp',
    'application/pdf',
  ];

  const errorSerialNumberNotUnique = {
    children: copyProvider.getCopy(
      'installer.AddAdditionalDetails.s18',
      'This serial number has previously been used'
    ),
  };

  const errorInvalidSerialNumberCharacter = {
    children: copyProvider.getCopy(
      'installer.AddAdditionalDetails.s19',
      'Serial number can contain letters and numbers only'
    ),
  };

  const errorInvalidSerialNumberLength = {
    children: copyProvider.getCopy(
      'installer.AddAdditionalDetails.s20',
      'A serial number must be between 5 and 50 characters'
    ),
  };

  const errorInvalidFileFormat = {
    children: copyProvider.getCopy(
      'installer.AddAdditionalDetails.s21',
      'Photograph must be one of the following file formats (JPG, JPEG, PNG, GIF, PDF or BMP)'
    ),
  };

  const errorInvalidFileSize = {
    children: copyProvider.getCopy(
      'installer.AddAdditionalDetails.s22',
      'Photograph cannot exceed 10MB in size'
    ),
  };

  const errorInvalidFileFormatAndSize = {
    children: copyProvider.getCopy(
      'installer.AddAdditionalDetails.s27',
      'Photograph must be one of the following file formats (JPG, JPEG, PNG, GIF, PDF or BMP) and cannot exceed 10MB in size'
    ),
  };

  const errorMinimumFilesUploadedApril = {
    children: copyProvider.getCopy(
      'installer.AddAdditionalDetails.s26',
      'You will need to upload a minimum of 4  photos. You can upload up to 10 photos as proof of installation.'
    ),
  };
  const errorMinimumFilesUploaded = {
    children: copyProvider.getCopy(
      'installer.AddAdditionalDetails.s32',
      'You must upload a minimum of 2 photos of the installed chargepoint.'
    ),
  };
  const errorWhileUploadingFile = {
    children: copyProvider.getCopy(
      'installer.AddAdditionalDetails.s27',
      'Something went wrong, please retry.(TBC)'
    ),
  };

  const errorMaximumFilesUploaded = {
    children: copyProvider.getCopy(
      'installer.AddAdditionalDetails.s23',
      'You can upload a maximum of 10 photos'
    ),
  };

  const errorBlankChargepointManufacturer = {
    children: copyProvider.getCopy(
      'installer.ChargepointsDetails.s9',
      'Select a manufacturer'
    ),
  };

  const errorBlankChargepointModel = {
    children: copyProvider.getCopy(
      'installer.ChargepointsDetails.s10',
      'Select a model'
    ),
  };

  const handleChargepointManufacturer = (value) => {
    setManufacturer(value);
  };

  const handleChargepointsModel = (value, numberOfSockets) => {
    setModel(value);
    setSocket(numberOfSockets);
  };

  const handleSerialNumber = (event) => {
    setSerialNumber(event.target.value);
  };

  const handleUploadedFile = async () => {
    let hasError = false;

    const uploadedFileValue = document.getElementById(
      'uploaded-additional-file'
    );
    setIndexId(indexId + 1);
    if (
      !validFileTypes.includes(uploadedFileValue?.files[0]?.type) &&
      uploadedFileValue.files[0]?.size > PHOTO_FILE_SIZE
    ) {
      setErrorUploadedFile(errorInvalidFileFormatAndSize);
      errorArray.push({
        ...errorInvalidFileFormatAndSize,
        href: '#uploaded-additional-file',
      });
      hasError = true;
    } else if (!validFileTypes.includes(uploadedFileValue?.files[0]?.type)) {
      setErrorUploadedFile(errorInvalidFileFormat);
      errorArray.push({
        ...errorInvalidFileFormat,
        href: '#uploaded-additional-file',
      });
      hasError = true;
    } else if (uploadedFileValue.files[0]?.size > PHOTO_FILE_SIZE) {
      setErrorUploadedFile(errorInvalidFileSize);
      errorArray.push({
        ...errorInvalidFileSize,
        href: '#uploaded-additional-file',
      });
      hasError = true;
    } else if (photos.length >= MAX_UPLOAD_FILES) {
      setErrorUploadedFile(errorMaximumFilesUploaded);
      errorArray.push({
        ...errorMaximumFilesUploaded,
        href: '#uploaded-additional-file',
      });
      hasError = true;
    } else {
      setErrorUploadedFile('');
      hasError = false;
      setErrorList([]);
    }

    if (!hasError) {
      const photo = await readFileAsDataURL(uploadedFileValue?.files[0]);

      setPhotos((prev) => [...prev, photo]);

      setTimeout(() => {
        uploadedFileValue.value = '';
      }, 0);
    } else {
      setErrorList(errorArray);
    }
  };

  const handleDelete = (event, photoIndex) => {
    setPhotos((prev) => prev.filter((value, index) => index !== photoIndex));
    deletePhoto({
      id: documentId,
      index: photoIndex,
      chargepoint_uid: chargePointRedux.uid,
    })
      .then((response) => {
        // eslint-disable-next-line no-console
        console.log(response.result);
        event.preventDefault();
      })
      .catch((error) => {
        // TODO: Show an error
        // eslint-disable-next-line no-console
        console.log(error);
        event.preventDefault();
      });
    event.preventDefault();
  };

  const handleBtnClick = () => {
    setHasAttemptedSave(true);
    let hasError = false;

    if (manufacturer === '') {
      errorArray.push({
        ...errorBlankChargepointManufacturer,
        href: '#chargepoint-manufacturer',
      });
      hasError = true;
    }

    if (model === '') {
      errorArray.push({
        errorBlankChargepointModel,
        href: '#chargepoint-model',
      });
      hasError = true;
    }

    if (hasChargepointSelectError) {
      hasError = true;
    }

    // chargepoint serial number
    if (serialNumber.length < 5 || serialNumber.length > 50) {
      setErrorSerialNumber(errorInvalidSerialNumberLength);
      errorArray.push({
        ...errorInvalidSerialNumberLength,
        href: '#number-chargepoints',
      });
      hasError = true;
    } else if (
      serialNumber !== '' &&
      !checkValidSerialNumber.test(serialNumber)
    ) {
      setErrorSerialNumber(errorInvalidSerialNumberCharacter);
      errorArray.push({
        ...errorInvalidSerialNumberCharacter,
        href: '#number-chargepoints',
      });
      hasError = true;
    } else {
      setErrorSerialNumber(null);
      errorArray.filter((error) => error.href === '#number-chargepoints');
    }

    // file uploads
    if (photos.length < MIN_UPLOAD_FILES) {
      setErrorUploadedFile(
        chargepointWordingChangeDate &&
          grantType === 'LANDLORD_RESIDENTIAL_GRANT'
          ? errorMinimumFilesUploadedApril
          : errorMinimumFilesUploaded
      );
      errorArray.push({
        ...(chargepointWordingChangeDate &&
        grantType === 'LANDLORD_RESIDENTIAL_GRANT'
          ? errorMinimumFilesUploadedApril
          : errorMinimumFilesUploaded),
        href: '#uploaded-additional-file',
      });
      hasError = true;
    }

    if (!hasError) {
      validateCpSerialNum({
        serial_number: serialNumber,
        chargepoint_manufacturer: manufacturer,
        chargepoint_model: model,
        document_id: documentId,
      })
        .then((res) => {
          if (res.data.success) {
            updateCpDetails({
              documentId,
              uid: chargePointRedux.uid,
              manufacturer,
              model,
              serialNumber,
              socket,
              photos,
            })
              .then((response) => {
                const { data, claimId } = response;
                const { chargepoint } = data;
                dispatch(setApplicationClaimId(claimId));
                dispatch(updateChargepointDetails(chargepoint));
                window.scrollTo(0, 0);
                history.push('/installer/chargepoints-summary');
              })
              .catch((err) => {
                setErrorUploadedFile(errorWhileUploadingFile);
                setErrorList([
                  ...errorList,
                  {
                    ...errorWhileUploadingFile,
                    href: '#uploaded-additional-file',
                  },
                ]);
                // eslint-disable-next-line no-console
                console.log('Error updating CP details:', err);
              });
          } else {
            setErrorSerialNumber(errorSerialNumberNotUnique);
            setErrorList([
              ...errorList,
              {
                ...errorSerialNumberNotUnique,
                href: '#number-chargepoints',
              },
            ]);
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.log(err);
        });
    } else {
      setErrorList(errorArray);
    }
  };

  const pageModel = (
    <>
      {errorList.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <h1 className="govuk-heading-xl">
        {copyProvider.getCopy(
          'installer.AddAdditionalDetails.s1',
          'Add serial number and photographs'
        )}
      </h1>
      {/* Charpoint + amounts --- come back to this */}
      <h2 className="govuk-heading-m">
        {copyProvider.getCopy(
          'installer.AddAdditionalDetails.s2',
          'Chargepoint 1'
        )}
      </h2>
      <ChargepointSelect
        withChargepointCount={false}
        manufacturer={manufacturer || ''}
        model={model || ''}
        onChangeManufacturer={handleChargepointManufacturer}
        onChangeModel={handleChargepointsModel}
        hasError={setHasChargepointSelectError}
        isSubmitted={hasAttemptedSave}
        setSelectChargepointError={setSelectChargepointError}
      />
      <Input
        errorMessage={errorSerialNumber || null}
        onChange={(event) => handleSerialNumber(event)}
        value={serialNumber}
        className="govuk-input--width-20"
        id="number-chargepoints"
        label={{
          children: copyProvider.getCopy(
            'installer.AddAdditionalDetails.s5',
            'Chargepoint serial number'
          ),
        }}
        name="number-chargepoints"
        type="text"
      />
      <h2 className="govuk-heading-s govuk-!-margin-top-8">
        {copyProvider.getCopy(
          'installer.AddAdditionalDetails.s6',
          'Upload photo evidence of the chargepoint you installed'
        )}
      </h2>
      <p className="govuk-body">
        {copyProvider.getCopy(
          'installer.AddAdditionalDetails.s8',
          'You need to provide:'
        )}
      </p>
      <ul className="govuk-body">
        <li>
          {copyProvider.getCopy(
            'installer.AddAdditionalDetails.s9',
            'a close-up photo of the chargepoint you installed'
          )}
        </li>
        {chargepointWordingChangeDate &&
          grantType === 'LANDLORD_RESIDENTIAL_GRANT' && (
            <li>
              {copyProvider.getCopy(
                'installer.AddAdditionalDetails.s30',
                'a photo of the chargepoint you installed showing the model and serial number.'
              )}
            </li>
          )}
        <li>
          {copyProvider.getCopy(
            'installer.AddAdditionalDetails.s10',
            'a photo of the chargepoint you installed and the associated parking space'
          )}
        </li>
        {chargepointWordingChangeDate &&
          grantType === 'LANDLORD_RESIDENTIAL_GRANT' && (
            <li>
              {copyProvider.getCopy(
                'installer.AddAdditionalDetails.s31',
                'a photo with a wide field view of the building where the chargepoint has been installed.'
              )}
            </li>
          )}
      </ul>
      <Details
        className="app-images-container"
        summaryChildren={copyProvider.getCopy(
          'installer.AddAdditionalDetails.s16',
          'Check example chargepoint photographs'
        )}
      >
        <div className="govuk-!-display-inline-block">
          <h4>
            {copyProvider.getCopy(
              'installer.AddAdditionalDetails.s11',
              'Chargepoint close-up'
            )}
          </h4>
          <img
            className="app-images-style"
            src={evcgClose}
            alt="example1 chargepoint photograph"
          />
        </div>
        <div className="govuk-!-display-inline-block">
          <h4>
            {copyProvider.getCopy(
              'installer.AddAdditionalDetails.s13',
              'Chargepoint and the parking space'
            )}
          </h4>
          <img
            className="app-images-style"
            src={evcgLong}
            alt="example2 chargepoint photograph"
          />
        </div>
      </Details>
      <p className="govuk-body">
        {chargepointWordingChangeDate &&
        grantType === 'LANDLORD_RESIDENTIAL_GRANT'
          ? copyProvider.getCopy(
              'installer.AddAdditionalDetails.s7',
              'Upload a minimum of 4 photos of the chargepoint. You can upload up to 10 photos as proof of installation.'
            )
          : copyProvider.getCopy(
              'installer.AddAdditionalDetails.s32',
              'You must upload a minimum of 2 photos of the installed chargepoint'
            )}
      </p>
      <Hint className="govuk-body">
        {copyProvider.getCopy(
          'installer.AddAdditionalDetails.s12',
          'The photos must be a JPG, JPEG, PNG, GIF, PDF or BMP file and each one can be up to 10MB in size.'
        )}
      </Hint>
      <div className="app-additional-images">
        <Input
          errorMessage={errorUploadedFile || null}
          id="uploaded-additional-file"
          className="app-no-border app-hidden"
          label={{
            children: (
              <span className="govuk-visually-hidden">
                {copyProvider.getCopy(
                  'installer.AddAdditionalDetails.s14',
                  'Upload a file'
                )}
              </span>
            ),
          }}
          type="file"
          accept=".jpg, .jpeg, .png, .gif, .bmp, .pdf, .doc"
          onChange={handleUploadedFile}
        />
      </div>
      <div className="govuk-button-group">
        <Button onClick={handleBtnClick}>
          {copyProvider.getCopy(
            'installer.AddAdditionalDetails.s15',
            'Save and return'
          )}
        </Button>
      </div>

      <div className="app-table-container">
        {photos &&
          photos.length > 0 &&
          photos.map((eachFile, photoIndex) => {
            return (
              <Table
                className="app-container-flex"
                rows={[
                  {
                    cells: [
                      {
                        children: (
                          <span key={nanoid()}>{eachFile.fileName}</span>
                        ),
                        className: 'app-table-cell-one-additional',
                      },
                      {
                        // Check this
                        children: (
                          <Link
                            to="/"
                            onClick={(event) => handleDelete(event, photoIndex)}
                          >
                            Delete
                          </Link>
                        ),
                        className: 'app-table-cell-two-additional',
                      },
                    ],
                  },
                ]}
              />
            );
          })}
      </div>
    </>
  );
  return <> {pageModel} </>;
};

export default AddAdditionalDetails;
